import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import './App.css';
import LampBuilder from './components/LampBuilder';
import Shop from './components/Shop';
import Contact from './components/Contact';
import About from './components/About';
import ProductDetail from './components/ProductDetail';
import ShippingPolicy from './components/ShippingPolicy';
import RefundPolicy from './components/RefundPolicy';
import TermsOfService from './components/TermsOfService';
import Cart from './components/Cart';
import Checkout from './components/Checkout';
import Dashboard from './components/Dashboard';
import Auth from './components/auth/Auth.jsx';
import SocialAuthCallback from './components/auth/SocialAuthCallback.jsx';
import AuthForm from './components/auth/AuthForm';
import LoginRequiredModal from './components/LoginRequiredModal';
import AdminRoute from './components/auth/AdminRoute';
import Admin from './components/Admin';
import { CartProvider } from './context/CartContext.jsx';
import { AuthProvider, useAuth } from './context/AuthContext.jsx';
import './components/auth/Auth.css';
import { getDisplayName } from './utils/userUtils';
import { getShortCommitHash } from './utils/commitHash';

// Import images
import lamp01 from './assets/home_featured_designs/lamp01.png';
import lamp02 from './assets/home_featured_designs/lamp02.png';
import lamp03 from './assets/home_featured_designs/lamp03.png';
import lamp04 from './assets/home_featured_designs/lamp04.png';
import lamp05 from './assets/home_featured_designs/lamp05.png';
import lamp06 from './assets/home_featured_designs/lamp06.png';
import lamp07 from './assets/home_featured_designs/lamp07.png';
import lamp08 from './assets/home_featured_designs/lamp08.png';
import lamp09 from './assets/home_featured_designs/lamp09.png';
import lamp10 from './assets/home_featured_designs/lamp10.png';
import lamp11 from './assets/home_featured_designs/lamp11.png';
import lamp12 from './assets/home_featured_designs/lamp12.png';

function Navigation({ openLoginModal }) {
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <header className="studio-header">
      <nav className="studio-navbar" aria-label="Main navigation">
        <div className="studio-brand-block">
          <Link to="/" className="studio-brand">LUMINA_3D</Link>
        </div>
        <div className="studio-nav-links">
          <Link 
            to="/about"
            className={`studio-nav-link${isActive('/about') ? ' active' : ''}`}
          >
            ABOUT
          </Link>
          <Link 
            to="/builder"
            className={`studio-nav-link${isActive('/builder') ? ' active' : ''}`}
          >
            DESIGN LAMP
          </Link>
          <Link 
            to="/shop"
            className={`studio-nav-link${isActive('/shop') ? ' active' : ''}`}
          >
            SHOP
          </Link>
          {isAdmin && (
            <Link 
              to="/admin"
              className={`studio-nav-link${isActive('/admin') ? ' active' : ''}`}
            >
              ADMIN
            </Link>
          )}
          {user ? (
            <Link 
              to="/dashboard"
              className="studio-nav-link"
            >
              {getDisplayName(user).toUpperCase()}
            </Link>
          ) : (
            <button
              className={`studio-nav-link${isActive('/auth') ? ' active' : ''}`}
              title="Login / Sign Up"
              onClick={() => openLoginModal('login')}
              style={{ background: 'none', border: 'none', padding: 0, margin: 0, cursor: 'pointer' }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
              </svg>
            </button>
          )}
          <Link 
            to="/cart"
            className={`studio-nav-link${isActive('/cart') ? ' active' : ''}`}
            title="Shopping Cart"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
              <path d="M19 6h-2c0-2.76-2.24-5-5-5S7 3.24 7 6H5c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zM12 3c1.66 0 3 1.34 3 3H9c0-1.66 1.34-3 3-3zm7 17H5V8h14v12z"/>
            </svg>
          </Link>
        </div>
      </nav>
    </header>
  );
}

function Home() {
  const carouselRef = React.useRef(null);
  const trackRef = React.useRef(null);

  // Use imported images
  const images = [
    lamp01,
    lamp02,
    lamp03,
    lamp04,
    lamp05,
    lamp06,
    lamp07,
    lamp08,
    lamp09,
    lamp10,
    lamp11,
    lamp12
  ];

  // Create a seamless loop by duplicating the images array
  const carouselImages = [...images, ...images, ...images];

  return (
    <>
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <h1>INTERACTIVE LAMP DESIGNER</h1>
          <Link to="/builder" className="cta-button">CREATE CUSTOM LAMP</Link>
          <p style={{ marginTop: '1.5rem', marginBottom: 0 }}>design-submit-print-deliver</p>
        </div>
      </section>

      {/* White Banner Below Hero/Carousel */}
      <section className="studio-white-banner">
        <div className="studio-white-banner-content">
          <h2 className="studio-white-banner-heading">Design and customize your own 3D-printed lamp with LUMINA_3D.</h2>
          <p className="studio-white-banner-subhead">Create unique lighting for your space—choose your shape, style, and features, then let us print and deliver your personalized lamp.</p>
        </div>
        <div className="studio-white-banner-image">
          <video
            src="/videos/my-3d-print.mp4"
            autoPlay
            loop
            muted
            playsInline
            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '10px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}
          >
            Sorry, your browser does not support embedded videos.
          </video>
        </div>
      </section>

      {/* Featured Designs Banner and Carousel moved here */}
      <div className="featured-banner">
        <span>FEATURED DESIGNS</span>
      </div>
      <section className="features" id="features">
        <div className="carousel-container" ref={carouselRef}>
          <div className="carousel-track" ref={trackRef}>
            {carouselImages.map((img, idx) => (
              <div className="carousel-image-box" key={idx}>
                <img src={img} alt={`featured lamp ${idx % images.length + 1}`} className="carousel-image" />
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function MainContent({ openLoginModal }) {
  const location = useLocation();
  const isBuilderPage = location.pathname === '/builder';

  return (
    <>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/builder" element={<LampBuilder openLoginModal={openLoginModal} />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/about" element={<About />} />
        <Route path="/product/:productId" element={<ProductDetail />} />
        <Route path="/shipping-policy" element={<ShippingPolicy />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/auth/callback" element={<SocialAuthCallback />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
      </Routes>

      {/* Footer - only show if not on builder page */}
      {!isBuilderPage && (
        <footer className="footer">
          <div className="footer-content">
            <div className="footer-section">
              <h4>follow us</h4>
              <div className="social-links">
                <button className="social-button">instagram</button>
              </div>
            </div>
            <div className="footer-section">
              <h4><Link to="/contact" style={{ color: 'inherit', textDecoration: 'none' }}>contact</Link></h4>
              <p>email: hello@illuma</p>
            </div>
            <div className="footer-section">
              <h4>legal bits</h4>
              <div className="policy-links">
                <Link to="/shipping-policy">shipping policy</Link>
                <Link to="/refund-policy">refund policy</Link>
                <Link to="/terms-of-service">terms of service</Link>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2024 illuma. all rights reserved. | {getShortCommitHash()}</p>
          </div>
        </footer>
      )}
    </>
  );
}

function App() {
  const [loginModalOpen, setLoginModalOpen] = React.useState(false);
  const [loginModalMode, setLoginModalMode] = React.useState('login'); // 'login' or 'required'
  const openLoginModal = (mode = 'login') => {
    setLoginModalMode(mode);
    setLoginModalOpen(true);
  };
  const closeLoginModal = () => setLoginModalOpen(false);

  // Modal wording
  const modalTitle = loginModalMode === 'required' ? 'Login Required' : 'Login';
  const modalDescription = loginModalMode === 'required'
    ? 'You must be logged in to save or load lamp designs.'
    : '';

  return (
    <AuthProvider>
      <CartProvider>
        <Router>
          <div className="App">
            <Navigation openLoginModal={openLoginModal} />
            <MainContent openLoginModal={openLoginModal} />
            {loginModalOpen && (
              <LoginRequiredModal
                title={modalTitle}
                description={modalDescription}
                onCancel={closeLoginModal}
              >
                <AuthForm mode={loginModalMode} onSuccess={closeLoginModal} onCancel={closeLoginModal} />
              </LoginRequiredModal>
            )}
          </div>
        </Router>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
